import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { usePostHog } from 'posthog-js/react'
import { useAuth } from '../hooks/useAuth'
import { authApi } from '../api/authApi'
import { UserEvents, identifyUser } from '@/lib/analytics'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertCircle,
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Palette,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  XCircle,
  Loader2,
} from 'lucide-react'
import { getErrorMessage } from '@/lib/utils'
import { env } from '@/config/env'
import { cn } from '@/lib/utils/cn'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'

// Combined schema for full form
const signUpSchema = z
  .object({
    email: z.string().email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string(),
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    artisticName: z.string().min(2, 'Artist name must be at least 2 characters').max(30, 'Artist name must be less than 30 characters'),
    bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type SignUpFormData = z.infer<typeof signUpSchema>

const STEPS = [
  { id: 'account', label: 'Account', icon: Mail },
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'artist', label: 'Artist Profile', icon: Palette },
]

function StepIndicator({
  currentStep,
  steps,
}: {
  currentStep: number
  steps: typeof STEPS
}) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep
        const isCurrent = index === currentStep
        const Icon = step.icon

        return (
          <div key={step.id} className="flex items-center">
            <div
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300',
                isCompleted && 'bg-primary border-primary text-primary-foreground',
                isCurrent && 'border-primary text-primary',
                !isCompleted && !isCurrent && 'border-muted-foreground/30 text-muted-foreground'
              )}
            >
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <Icon className="h-5 w-5" />
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'w-12 h-0.5 mx-2 transition-all duration-300',
                  index < currentStep ? 'bg-primary' : 'bg-muted-foreground/20'
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

interface AvailabilityIndicatorProps {
  isChecking: boolean
  isAvailable: boolean | null
  isError?: boolean
  message?: string
}

function AvailabilityIndicator({ isChecking, isAvailable, isError, message }: AvailabilityIndicatorProps) {
  if (isChecking) {
    return (
      <span className="flex items-center gap-1 text-muted-foreground text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking availability...
      </span>
    )
  }

  // A failed check used to render nothing at all, so a network blip looked identical to a field
  // nobody had typed in yet — the spinner simply vanished. Say so instead, and say it is not fatal:
  // the check is advisory, and the server still enforces uniqueness on submit.
  if (isError) {
    return (
      <span className="flex items-center gap-1 text-amber-600 text-xs">
        <AlertCircle className="h-3 w-3" />
        Couldn't check — you can continue
      </span>
    )
  }

  if (isAvailable === true) {
    return (
      <span className="flex items-center gap-1 text-green-600 text-xs">
        <CheckCircle2 className="h-3 w-3" />
        {message || 'Available'}
      </span>
    )
  }

  if (isAvailable === false) {
    return (
      <span className="flex items-center gap-1 text-destructive text-xs">
        <XCircle className="h-3 w-3" />
        {message || 'Not available'}
      </span>
    )
  }

  return null
}

export function SignUp() {
  const navigate = useNavigate()
  const posthog = usePostHog()
  const { register, isRegistering, registerError } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showTurnstileDialog, setShowTurnstileDialog] = useState(false)
  const [turnstileStatus, setTurnstileStatus] = useState<
    'pending' | 'verifying' | 'success' | 'error'
  >('pending')
  const [registrationSuccess, setRegistrationSuccess] = useState(false)

  // Availability state
  const [emailAvailability, setEmailAvailability] = useState<{
    checking: boolean
    available: boolean | null
    error?: boolean
    message?: string
  }>({ checking: false, available: null })
  
  const [artistNameAvailability, setArtistNameAvailability] = useState<{
    checking: boolean
    available: boolean | null
    error?: boolean
    message?: string
  }>({ checking: false, available: null })

  const form = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      artisticName: '',
      bio: '',
    },
    mode: 'onChange',
  })

  const emailValue = form.watch('email')
  const artistNameValue = form.watch('artisticName')
  
  const debouncedEmail = useDebouncedValue(emailValue, 500)
  const debouncedArtistName = useDebouncedValue(artistNameValue, 500)

  // Check email availability
  useEffect(() => {
    const checkEmail = async () => {
      if (!debouncedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(debouncedEmail)) {
        setEmailAvailability({ checking: false, available: null })
        return
      }

      setEmailAvailability({ checking: true, available: null })
      
      try {
        const result = await authApi.checkEmailAvailable(debouncedEmail)
        setEmailAvailability({
          checking: false,
          available: result.available,
          message: result.message,
        })
      } catch {
        setEmailAvailability({ checking: false, available: null, error: true })
      }
    }

    checkEmail()
  }, [debouncedEmail])

  // Check artist name availability
  useEffect(() => {
    const checkArtistName = async () => {
      if (!debouncedArtistName || debouncedArtistName.length < 2) {
        setArtistNameAvailability({ checking: false, available: null })
        return
      }

      setArtistNameAvailability({ checking: true, available: null })
      
      try {
        const result = await authApi.checkArtistNameAvailable(debouncedArtistName)
        setArtistNameAvailability({
          checking: false,
          available: result.available,
          message: result.message,
        })
      } catch {
        setArtistNameAvailability({ checking: false, available: null, error: true })
      }
    }

    checkArtistName()
  }, [debouncedArtistName])

  const validateCurrentStep = async () => {
    switch (currentStep) {
      case 0:
        const step0Valid = await form.trigger(['email', 'password', 'confirmPassword'])
        // Also check email availability
        if (step0Valid && emailAvailability.available === false) {
          form.setError('email', { message: 'This email is already registered' })
          return false
        }
        return step0Valid
      case 1:
        return await form.trigger(['firstName', 'lastName'])
      case 2:
        const step2Valid = await form.trigger(['artisticName', 'bio'])
        // Also check artist name availability
        if (step2Valid && artistNameAvailability.available === false) {
          form.setError('artisticName', { message: 'This artist name is already taken' })
          return false
        }
        return step2Valid
      default:
        return true
    }
  }

  const handleNext = async () => {
    const isValid = await validateCurrentStep()
    if (isValid && currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async (data: SignUpFormData) => {
    // Final availability check before submission
    if (emailAvailability.available === false) {
      form.setError('email', { message: 'This email is already registered' })
      setCurrentStep(0)
      return
    }
    if (artistNameAvailability.available === false) {
      form.setError('artisticName', { message: 'This artist name is already taken' })
      setCurrentStep(2)
      return
    }

    if (env.turnstileSiteKey) {
      setTurnstileStatus('verifying')
      setShowTurnstileDialog(true)
    } else {
      await submitRegistration(data)
    }
  }

  const submitRegistration = async (data: SignUpFormData, turnstileToken?: string) => {
    try {
      const user = await register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        artisticName: data.artisticName,
        bio: data.bio,
        turnstileToken,
      })

      // Registration doesn't establish a session (the artist signs in
      // separately from the success screen below), so this is the only place
      // that ever sees the moment of signup — identify right away rather than
      // waiting for the eventual first sign-in to link the anonymous session
      // that actually did the signing up.
      identifyUser(posthog, user)
      posthog?.capture(UserEvents.SIGNED_UP, {
        role: user.role,
        has_artistic_name: !!user.artisticName,
      })

      setRegistrationSuccess(true)
    } catch {
      // Error handled by mutation
    }
  }

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileStatus('success')
    setTimeout(() => {
      submitRegistration(form.getValues(), token)
      setShowTurnstileDialog(false)
    }, 500)
  }

  const handleTurnstileError = () => {
    setTurnstileStatus('error')
  }

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
    }
  }

  // Success state
  if (registrationSuccess) {
    return (
      <div className="w-full text-center">
        <div className="mb-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
          <h2 className="text-2xl font-semibold mb-2">Welcome to Arthaus!</h2>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            Your artist account has been created successfully. You can now sign in and start managing your portfolio.
          </p>
        </div>

        <div className="space-y-4">
          <Button className="w-full h-12 text-base font-medium" onClick={() => navigate('/')}>
            Sign In to Your Account
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
        <h2 className="text-2xl font-semibold mb-2">Join as an Artist</h2>
        <p className="text-muted-foreground text-sm">
          Create your account and start showcasing your work
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} steps={STEPS} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          {registerError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(registerError)}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Account Details */}
          {currentStep === 0 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Mail className="h-4 w-4" />
                  Step 1: Account Details
                </div>
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          type="email"
                          placeholder="artist@example.com"
                          {...field}
                          disabled={isRegistering}
                          className={cn(
                            'pl-10 h-12',
                            emailAvailability.available === false && 'border-destructive focus-visible:ring-destructive'
                          )}
                        />
                      </div>
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <FormMessage />
                      <AvailabilityIndicator
                        isChecking={emailAvailability.checking}
                        isAvailable={emailAvailability.available}
                        isError={emailAvailability.error}
                        message={emailAvailability.message}
                      />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...field}
                          disabled={isRegistering}
                          className="pl-10 pr-10 h-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">
                      At least 8 characters with uppercase, lowercase, and a number
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">
                      Confirm Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          {...field}
                          disabled={isRegistering}
                          className="pl-10 pr-10 h-12"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Step 2: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <User className="h-4 w-4" />
                  Step 2: Personal Information
                </div>
              </div>

              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          placeholder="Jane"
                          {...field}
                          disabled={isRegistering}
                          className="pl-10 h-12"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">Last Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          placeholder="Doe"
                          {...field}
                          disabled={isRegistering}
                          className="pl-10 h-12"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Step 3: Artist Profile */}
          {currentStep === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  <Palette className="h-4 w-4" />
                  Step 3: Artist Profile
                </div>
              </div>

              <FormField
                control={form.control}
                name="artisticName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">
                      Artist Name
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          placeholder="Your artistic name or moniker"
                          {...field}
                          disabled={isRegistering}
                          className={cn(
                            'pl-10 h-12',
                            artistNameAvailability.available === false && 'border-destructive focus-visible:ring-destructive'
                          )}
                        />
                      </div>
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <FormDescription className="text-xs">
                        This is how you'll be known on Arthaus
                      </FormDescription>
                      <AvailabilityIndicator
                        isChecking={artistNameAvailability.checking}
                        isAvailable={artistNameAvailability.available}
                        isError={artistNameAvailability.error}
                        message={artistNameAvailability.message}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-foreground">
                      Bio{' '}
                      <span className="font-normal text-muted-foreground text-xs">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us about yourself and your art..."
                        {...field}
                        disabled={isRegistering}
                        className="min-h-[100px] resize-none"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      You can always add or update this later
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4">
            {currentStep > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={isRegistering}
                className="flex-1 h-12"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>
            )}

            {currentStep < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={isRegistering || (currentStep === 0 && emailAvailability.checking)}
                className={cn('h-12', currentStep === 0 ? 'w-full' : 'flex-1')}
              >
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button 
                type="submit" 
                disabled={isRegistering || artistNameAvailability.checking || artistNameAvailability.available === false} 
                className="flex-1 h-12"
              >
                {isRegistering ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <CheckCircle2 className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/" className="text-foreground hover:underline font-medium">
              Sign in
            </Link>
          </div>
        </form>
      </Form>

      {/* Turnstile Verification Dialog */}
      <Dialog open={showTurnstileDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Security Verification
            </DialogTitle>
            <DialogDescription>
              Please complete the security check to create your account.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-4">
            {turnstileStatus === 'verifying' && (
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Turnstile
                    siteKey={env.turnstileSiteKey}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileError}
                  />
                </div>
                <p className="text-sm text-muted-foreground">Verifying you're human...</p>
              </div>
            )}

            {turnstileStatus === 'success' && (
              <div className="text-center py-4">
                <div className="flex justify-center mb-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-green-600">Verification successful!</p>
                <p className="text-sm text-muted-foreground mt-1">Creating your account...</p>
              </div>
            )}

            {turnstileStatus === 'error' && (
              <div className="text-center py-4">
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>Verification failed. Please try again.</AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  onClick={() => setTurnstileStatus('verifying')}
                  className="mr-2"
                >
                  Try Again
                </Button>
                <Button variant="ghost" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {turnstileStatus === 'verifying' && (
            <div className="flex justify-center">
              <Button variant="ghost" size="sm" onClick={() => handleDialogClose(false)}>
                Cancel and go back
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
